import { appService, initAuth, initBookingService } from '@ibe/components';
import {
  AppService,
  ConfigModel,
  ConfigService,
  defaultConfig,
  fetchJSON,
  LoggerFactory
} from '@ibe/services';

import dayjs from 'dayjs';
import de from 'date-fns/locale/de';
import de_AT from 'date-fns/locale/de-AT';
import fr_CH from 'date-fns/locale/fr-CH';

import { registerLocale, setDefaultLocale } from 'react-datepicker';
import { Currency, getLanguageFromTenant, Language } from '../Translations';
import { enableTracking } from '../Tracking/setup';
import trackingSubscriptions from '../Tracking/trackingSubscriptions';
import { ThgConfigModel } from './ThgConfigModel';

import { packageIdSessionStorage } from '../Pages/Util/utils';
import { accountingAreaHeaderName, customConfig } from './customConfig';
import { accountingAreaStorage } from '../Util/globals';

const logger = LoggerFactory.get('config');

defaultConfig.traveler.genderValues = []; // workaround cause "deepmerge" doesnt replace values in array

// setting both durations in customConfig would be better, but at the moment the merge afterwards would be incorrect
const durationOptions = {
  WEEKS: [1, 2, 3, 4],
  NIGHTS: [...Array.from({ length: 28 }, (_, i) => i + 1).filter(i => i > 2 && (i < 22 || i > 27))]
};
defaultConfig.exactSearchDurationOptions = durationOptions;
defaultConfig.packageSearchDurationOptions = durationOptions;

export const configService = new ConfigService<ConfigModel>(ConfigModel, defaultConfig);
configService.mergeIntoConfig(customConfig);

function initTracking(config: ConfigModel): void {
  logger.log('tracking initialized...');
  enableTracking(config);
}

function initSubscriptions() {
  trackingSubscriptions();
}

function getLocaleObject(lang: string) {
  switch (lang) {
    case Language.DE_AT:
      return de_AT;
    case Language.fr_CH:
      return fr_CH;
    default:
      return de;
  }
}

function initLanguage(initTenant?: string | null): void {
  const language = getLanguageFromTenant(initTenant);
  const localeData = getLocaleObject(language);
  registerLocale(language, localeData);

  appService.setLang(language);
  dayjs.locale(language);
  setDefaultLocale(language);
}

export const initAccountingAreaHeader = (
  appServiceProp: AppService,
  thgConfig?: ThgConfigModel,
  isWhiteLabel?: boolean
) => {
  const accountingArea = thgConfig?.accountingArea ?? accountingAreaStorage.get() ?? '';
  if (accountingArea && !isWhiteLabel) {
    appServiceProp.setApiHeaders({
      [accountingAreaHeaderName]: accountingArea
    });
  }
};

function initCurrency(currency?: string | null): void {
  appService.setCurrency(currency ?? Currency.EUR);
}

export const boot = async (
  isWhiteLabel?: boolean,
  keyPrefix?: string,
  configOverrides?: Partial<ConfigModel>,
  skipInitBooking?: boolean,
  thgConfig?: ThgConfigModel
): Promise<boolean> => {
  if (configOverrides) {
    configService.mergeIntoConfig(configOverrides);
  }
  const url = configService.get().configUrl;
  let externalConfig = {};
  if (url) {
    externalConfig = (await fetchJSON(url)) as Partial<ConfigModel>;
  }
  configService.mergeIntoConfig(externalConfig);
  if (thgConfig) {
    configService.mergeIntoConfig(thgConfig);
  }
  const config = configService.get();
  logger.log('booting...');
  await appService.hydrate(config);
  await initAuth(config);

  initLanguage(config?.tenant);
  initCurrency(config?.currency);
  initAccountingAreaHeader(appService, thgConfig, isWhiteLabel);
  if (!isWhiteLabel) {
    initTracking(config);
  }
  initSubscriptions();

  let bookingError = false;
  if (!skipInitBooking) {
    const packageId = packageIdSessionStorage.get()?.packageId;
    try {
      await initBookingService(
        config,
        appService.api,
        true,
        !!keyPrefix ? keyPrefix : undefined,
        packageId,
        true
      );
    } catch (err) {
      console.error(err);
      bookingError = true;
    }
  }

  logger.log('App hydrated', appService.currency, appService.lang);
  return bookingError;
};
